import * as Headless from '@headlessui/react';
import React from 'react';
import { Loader2 } from 'lucide-react';

interface ConfirmState {
  params?: {
    title?: string;
    body?: string;
    confirmText?: string;
    pendingText?: string;
  };
}

interface DataGridConfirmModalProps {
  confirmState: ConfirmState | null | undefined;
  confirmProceed: () => Promise<void>;
  cancelConfirm: () => void;
  themeBorder: string;
  t: (key: string) => string;
}

export function DataGridConfirmModal({
  confirmState,
  confirmProceed,
  cancelConfirm,
  themeBorder,
  t
}: DataGridConfirmModalProps) {
  const [pending, setPending] = React.useState(false);
  const pendingRef = React.useRef(false);
  const close = () => {
    if (!pendingRef.current) cancelConfirm();
  };
  const proceed = async () => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    setPending(true);
    try {
      await confirmProceed();
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  };
  return (
    <Headless.Transition appear show={!!confirmState} as={React.Fragment}>
      <Headless.Dialog as="div" className="relative z-50" onClose={close}>
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Headless.Dialog.Panel
              className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-zinc-900 p-6 text-left align-middle shadow-xl transition-all"
              style={{ border: `1px solid ${themeBorder}` }}
            >
              <Headless.Dialog.Title
                as="h3"
                className="text-lg font-medium leading-6 text-zinc-900 dark:text-zinc-100"
              >
                {confirmState?.params?.title ?? 'Confirm'}
              </Headless.Dialog.Title>

              {confirmState?.params?.body && (
                <div className="mt-2">
                  <p className="text-sm text-zinc-700 dark:text-zinc-300">
                    {confirmState.params.body}
                  </p>
                </div>
              )}

              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  className="rounded-2xl px-3 py-1.5 text-sm ring-1 ring-zinc-300 dark:ring-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800"
                  onClick={close}
                  disabled={pending}
                >
                  {t('cancel') || 'Cancel'}
                </button>
                <button
                  type="button"
                  className="rounded-2xl px-3 py-1.5 text-sm ring-1 ring-indigo-300 text-indigo-700 dark:ring-indigo-700/60 dark:text-indigo-300 hover:bg-indigo-50/60 dark:hover:bg-indigo-900/20"
                  onClick={proceed}
                  disabled={pending}
                  aria-busy={pending}
                >
                  {pending && (
                    <Loader2
                      aria-hidden="true"
                      className="mr-2 inline h-4 w-4 animate-spin"
                    />
                  )}
                  {pending
                    ? confirmState?.params?.pendingText || t('loading')
                    : confirmState?.params?.confirmText ||
                      t('confirm') ||
                      'Confirm'}
                </button>
              </div>
            </Headless.Dialog.Panel>
          </div>
        </div>
      </Headless.Dialog>
    </Headless.Transition>
  );
}
